import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  RawFamilySearchPersonResults,
  formattedFamilySearchData,
} from '@ltvco/refresh-lib/fmt';
import {
  Box,
  Text,
  Button,
  styled,
  Stack,
  Divider,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { nameize, useScreenSize, DateUtil } from '@ltvco/refresh-lib/utils';
import {
  Record as RecordType,
  reportGetters,
  SerializedContactTeaserData,
  SerializedObituaryDetails,
  SerializedObituaryTeaserRecord,
  ContactDetail,
} from '@ltvco/refresh-lib/v1';
import {
  AdvancedSearchURLParams,
  CollectionType,
  PersonSearchParamsv2,
} from '@ltvco/refresh-lib/v2';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Person,
  Tune,
  LocalFlorist,
  FamilyRestroom,
  Work,
  Balance,
  Gavel,
} from '@mui/icons-material';
import React, { useContext, useEffect, useState } from 'react';

import { constants } from 'appConstants';
import { ReportChatCard, SourceSelect } from 'components/ChatBV/components';
import {
  NUMBER_OF_TEASER_RESULTS_TO_SHOW,
  CHATBV_GA4_EVENTS,
  COLLECTION_TYPE_LABELS,
} from 'components/ChatBV/constants';
import { PersonParamsFunctionArguments } from 'components/ChatBV/types';
import {
  OpinionResult,
  PACERDocketResult,
} from 'routes/Dashboard/CourtCases/types';
import { cleanOpinionType } from 'routes/Dashboard/CourtCases/utils';
import { formatShorthandDate } from 'routes/SearchPerson/Cards/NewCardsDesignVariation1/common/utils';
import { ContactFetcher } from 'routes/SearchPerson/Fetchers/ContactFetcher';
import { CourtCaseFetcher } from 'routes/SearchPerson/Fetchers/CourtCaseFetcher';
import { CourtOpinionFetcher } from 'routes/SearchPerson/Fetchers/CourtOpinionFetcher';
import { FamilySearchFetcher } from 'routes/SearchPerson/Fetchers/FamilySearchFetcher';
import { ObituaryFetcher } from 'routes/SearchPerson/Fetchers/ObituaryFetcher';
import { PersonFetcher } from 'routes/SearchPerson/Fetchers/PersonFetcher';

type MessageTeaserProps = {
  teaser: RecordType[];
  handleChatTeaserClick: (bvid: string) => void;
  handleObituaryClick: (permalink: string) => void;
  handleAncestorClick: (permalink: string) => void;
  handleContactClick: (permalink: string) => void;
  currentBvid: string;
  reportOpen: boolean;
  searchData: AdvancedSearchURLParams;
  params?: PersonParamsFunctionArguments;
  collectionType: CollectionType;
  setCollectionType: (collectionType: CollectionType) => void;
  handleFiltersClick: (params: PersonSearchParamsv2) => void;
};

const StyledStack = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const StyledText = styled(Text)(() => ({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}));

const StyledSpan = styled('span')(() => ({
  fontWeight: 'bold',
}));

export const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  color: theme.palette.text.secondary,
  borderColor: theme.palette.text.secondary,

  [theme.breakpoints.down('sm')]: {
    height: '50px',
  },
}));

const CollapsedButton = styled(Button)(({ theme }) => ({
  border: 'none',
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '&:focus': {
    backgroundColor: 'transparent',
  },
  '&:active': {
    backgroundColor: 'transparent',
  },
}));

type TeaserFieldProps = {
  label: string;
  value: React.ReactNode;
  condition?: boolean;
};

const TeaserField = ({ label, value, condition = true }: TeaserFieldProps) => {
  if (!condition) {
    return null;
  }

  return (
    <Text variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
      <Text component="span" sx={{ fontWeight: 600 }}>
        {label}:
      </Text>{' '}
      {value}
    </Text>
  );
};

export const collectionTypeIcons: { [key: string]: React.ReactNode } = {
  [CollectionType.PUBLIC_RECORDS]: <Person />,
  [CollectionType.OBITUARIES]: <LocalFlorist />,
  [CollectionType.FAMILY_SEARCH]: <FamilyRestroom />,
  [CollectionType.CONTACTS]: <Work />,
  [CollectionType.COURT_OPINIONS]: <Gavel />,
  [CollectionType.COURT_CASES]: <Balance />,
};

export const MessageTeaser = ({
  teaser,
  handleChatTeaserClick,
  handleObituaryClick,
  handleAncestorClick,
  handleContactClick,
  currentBvid,
  reportOpen,
  searchData,
  params,
  collectionType,
  setCollectionType,
  handleFiltersClick,
}: MessageTeaserProps) => {
  const { trackEventGA4, logError } = useContext(AppConfig);
  const [collapsed, setCollapsed] = useState(false);
  const [amountOfResultsToShow, setAmountOfResultsToShow] = useState<number>(
    NUMBER_OF_TEASER_RESULTS_TO_SHOW
  );
  const [personResults, setPersonResults] = useState<RecordType[] | undefined>(
    teaser || []
  );
  const [obituaryResults, setObituaryResults] = useState<
    SerializedObituaryDetails | undefined
  >(undefined);
  const [familySearchResults, setFamilySearchResults] = useState<
    RawFamilySearchPersonResults | undefined
  >(undefined);
  const [contactResults, setContactResults] = useState<
    SerializedContactTeaserData | undefined
  >(undefined);
  const [courtCaseResults, setCourtCaseResults] = useState<PACERDocketResult[]>(
    []
  );
  const [courtOpinionResults, setCourtOpinionResults] = useState<
    OpinionResult[]
  >([]);
  const [localSearchData, setLocalSearchData] =
    useState<AdvancedSearchURLParams>({
      ...searchData,
      lName: params?.last_name || '',
      fName: params?.first_name || '',
      mName: params?.middle_name || '',
      fullName: [params?.first_name, params?.middle_name, params?.last_name]
        .filter(Boolean)
        .join(' '),
    });

  const theme = useTheme();

  const { isMobile } = useScreenSize();

  const resultsArraysByCollectionType: {
    [key: string]:
      | RecordType[]
      | SerializedObituaryTeaserRecord[]
      | ContactDetail[]
      | PACERDocketResult[]
      | OpinionResult[];
  } = {
    [CollectionType.PUBLIC_RECORDS]: personResults?.length
      ? personResults
      : teaser,
    [CollectionType.OBITUARIES]:
      obituaryResults || ([] as SerializedObituaryTeaserRecord[]),
    [CollectionType.FAMILY_SEARCH]: familySearchResults
      ? formattedFamilySearchData(
          familySearchResults,
          params?.first_name || '',
          params?.last_name || ''
        ).map((item) => ({
          ...item,
          birthInfo: item.birthInfo?.trim() || '',
          relatives:
            (item.relatives?.filter((relative) =>
              Boolean(relative)
            ) as string[]) || [],
          aliases:
            (item.aliases?.filter((alias) => Boolean(alias)) as string[]) || [],
          locations:
            (item.locations?.filter((location) =>
              Boolean(location)
            ) as string[]) || [],
          bvid: item.familySearchId || '',
        }))
      : ([] as RecordType[]),
    [CollectionType.CONTACTS]:
      contactResults?.contacts || ([] as ContactDetail[]),
    [CollectionType.COURT_CASES]:
      courtCaseResults || ([] as PACERDocketResult[]),
    [CollectionType.COURT_OPINIONS]:
      courtOpinionResults || ([] as OpinionResult[]),
  };

  const renderFunctionByCollectionType: {
    [key: string]: (
      recordItem:
        | RecordType
        | SerializedObituaryTeaserRecord
        | ContactDetail
        | PACERDocketResult
        | OpinionResult,
      index: number
    ) => React.ReactNode;
  } = {
    [CollectionType.PUBLIC_RECORDS]: (
      recordItem:
        | RecordType
        | SerializedObituaryTeaserRecord
        | ContactDetail
        | PACERDocketResult
        | OpinionResult,
      index: number
    ) => {
      const item = recordItem as RecordType;
      return (
        <ReportChatCard
          title={item.name || ''}
          reportType="person"
          clickAction={() => handleChatPersonTeaserClick(item.bvid || '')}
          description={
            <Stack spacing={0.5}>
              {item.birthInfo && (
                <StyledText color={'text.secondary'}>
                  <StyledSpan>Born:</StyledSpan>
                  {` ${item.birthInfo}`}
                </StyledText>
              )}
              {item?.locations?.length ? (
                <StyledText color={'text.secondary'}>
                  <StyledSpan>Locations:</StyledSpan>
                  {` ${item.locations
                    .filter((location) => Boolean(location))
                    .join(' • ')}`}
                </StyledText>
              ) : null}
              {item.relatives?.length ? (
                <StyledText color={'text.secondary'}>
                  <StyledSpan>Relatives:</StyledSpan>
                  {` ${item.relatives
                    .filter((relative) => Boolean(relative))
                    .map((relative) => nameize(relative))
                    .join(', ')}`}
                </StyledText>
              ) : null}
              {item.aliases?.length ? (
                <StyledText color={'text.secondary'}>
                  <StyledSpan>Aliases:</StyledSpan>
                  {` ${item.aliases
                    .filter((alias) => Boolean(alias))
                    .map((alias) => nameize(alias))
                    .join(', ')}`}
                </StyledText>
              ) : null}
            </Stack>
          }
          disabled={currentBvid === item.bvid && reportOpen}
          key={item.bvid || `teaser-${index}`}
        />
      );
    },
    [CollectionType.OBITUARIES]: (
      recordItem:
        | RecordType
        | SerializedObituaryTeaserRecord
        | ContactDetail
        | PACERDocketResult
        | OpinionResult,
      index: number
    ) => {
      const item = recordItem as SerializedObituaryTeaserRecord;
      return (
        <ReportChatCard
          title={item.name || ''}
          reportType="obituary"
          clickAction={() => handleObituaryTeaserClick(item.id || '')}
          description={
            <Stack spacing={0.5}>
              {item.dob && formatShorthandDate(item.dob) && (
                <StyledText color={'text.secondary'}>
                  <StyledSpan>Date of Birth:</StyledSpan>
                  {` ${formatShorthandDate(item.dob)}`}
                </StyledText>
              )}
              {item.dod && formatShorthandDate(item.dod) && (
                <StyledText color={'text.secondary'}>
                  <StyledSpan>Date of Death:</StyledSpan>
                  {` ${formatShorthandDate(item.dod)}`}
                </StyledText>
              )}
              {(item.city || item.state) && (
                <StyledText color={'text.secondary'}>
                  <StyledSpan>Location:</StyledSpan>
                  {` ${
                    item.city && item.state
                      ? `${item.city}, ${item.state}`
                      : item.city || item.state
                  }`}
                </StyledText>
              )}
            </Stack>
          }
          disabled={false}
          key={item.id || `teaser-${index}`}
        />
      );
    },
    [CollectionType.FAMILY_SEARCH]: (
      recordItem:
        | RecordType
        | SerializedObituaryTeaserRecord
        | ContactDetail
        | PACERDocketResult
        | OpinionResult,
      index: number
    ) => {
      const item = recordItem as RecordType;

      return (
        <ReportChatCard
          title={item.name || ''}
          reportType="ancestor"
          clickAction={() =>
            handleFamilySearchTeaserClick(item.familySearchId || '')
          }
          description={
            <Stack spacing={0.5}>
              {item.birthInfo && (
                <StyledText color={'text.secondary'}>
                  <StyledSpan>Born:</StyledSpan>
                  {` ${item.birthInfo}`}
                </StyledText>
              )}
              {item?.locations?.length ? (
                <StyledText color={'text.secondary'}>
                  <StyledSpan>Locations:</StyledSpan>
                  {` ${item.locations
                    .filter((location) => Boolean(location))
                    .join(' • ')}`}
                </StyledText>
              ) : null}
              {item.relatives?.length ? (
                <StyledText color={'text.secondary'}>
                  <StyledSpan>Relatives:</StyledSpan>
                  {` ${item.relatives
                    .filter((relative) => Boolean(relative))
                    .map((relative) => nameize(relative))
                    .join(', ')}`}
                </StyledText>
              ) : null}
            </Stack>
          }
          disabled={false}
          key={item.bvid || `teaser-${index}`}
        />
      );
    },
    [CollectionType.CONTACTS]: (
      recordItem:
        | RecordType
        | SerializedObituaryTeaserRecord
        | ContactDetail
        | PACERDocketResult
        | OpinionResult,
      index: number
    ) => {
      const item = recordItem as ContactDetail;
      return (
        <ReportChatCard
          title={item.name || ''}
          reportType="contact"
          clickAction={() => handleContactTeaserClick(item.id || '')}
          description={
            <Stack spacing={0.5}>
              {item.title && (
                <StyledText color={'text.secondary'}>
                  <StyledSpan>Job Title:</StyledSpan>
                  {` ${item.title}`}
                </StyledText>
              )}
              {item.address && (
                <StyledText color={'text.secondary'}>
                  <StyledSpan>Location:</StyledSpan>
                  {` ${item.address}`}
                </StyledText>
              )}
              {item.companies?.length ? (
                <StyledText color={'text.secondary'}>
                  <StyledSpan>Companies:</StyledSpan>
                  {` ${item.companies
                    .map((company) => nameize(company.name))
                    .join(' • ')}`}
                </StyledText>
              ) : null}
            </Stack>
          }
          disabled={false}
          key={item.id || `teaser-${index}`}
        />
      );
    },
    [CollectionType.COURT_CASES]: (
      recordItem:
        | RecordType
        | SerializedObituaryTeaserRecord
        | ContactDetail
        | PACERDocketResult
        | OpinionResult,
      index: number
    ) => {
      const item = recordItem as PACERDocketResult;

      const dateUtil = new DateUtil();
      const filedDate = item.dateFiled
        ? dateUtil.parseDateFromString(item.dateFiled, 'MM/dd/yyyy') ?? null
        : null;
      const terminatedDate = item.dateTerminated
        ? dateUtil.parseDateFromString(item.dateTerminated, 'MM/dd/yyyy') ??
          null
        : null;

      const dateField =
        filedDate || terminatedDate ? (
          <Box sx={{ mb: 0.5 }}>
            <Text variant="body2" color="text.primary">
              {filedDate && (
                <>
                  <Text component="span" sx={{ fontWeight: 600 }}>
                    Filed:
                  </Text>{' '}
                  {filedDate}
                </>
              )}
              {filedDate && terminatedDate && (
                <Text component="span" sx={{ fontWeight: 600 }}>
                  {' '}
                  |{' '}
                </Text>
              )}
              {terminatedDate && (
                <>
                  <Text component="span" sx={{ fontWeight: 600 }}>
                    Terminated:
                  </Text>{' '}
                  {terminatedDate}
                </>
              )}
            </Text>
          </Box>
        ) : null;

      const docketField = (
        <Box sx={{ mb: 0.5 }}>
          <Text variant="body2" color="text.primary">
            <Text component="span" sx={{ fontWeight: 600 }}>
              Docket:
            </Text>{' '}
            {item.docketNumber}
            <Text component="span" sx={{ fontWeight: 600 }}>
              {' '}
              | Court:
            </Text>{' '}
            {item.court}
          </Text>
        </Box>
      );

      const simpleFields = [
        {
          label: 'Nature of Suit',
          value: item.suitNature,
          condition: Boolean(item.suitNature),
        },
        {
          label: 'Assigned to',
          value: item.assignedTo,
          condition: Boolean(item.assignedTo),
        },
        {
          label: 'Parties',
          value: item.party?.join(', '),
          condition: Boolean(item.party && item.party.length > 0),
        },
        {
          label: 'Attorneys',
          value: item.attorney?.join(', '),
          condition: Boolean(item.attorney && item.attorney.length > 0),
        },
      ];

      const courtCaseTeaserHandler = () => {
        if (item.docket_id !== null && item.docket_id !== undefined) {
          handleCourtCaseTeaserClick(item.docket_id.toString());
        }
      };

      return (
        <ReportChatCard
          title={item.caseName || ''}
          reportType="court-cases"
          icon={<Balance color="primary" fontSize="small" />}
          clickAction={courtCaseTeaserHandler}
          description={
            <Stack spacing={0.5}>
              {dateField}
              {docketField}
              {simpleFields.map((field, fieldIndex) => (
                <TeaserField
                  key={fieldIndex}
                  label={field.label}
                  value={field.value}
                  condition={field.condition}
                />
              ))}
            </Stack>
          }
          disabled={false}
          key={item.docket_id || `teaser-${index}`}
        />
      );
    },
    [CollectionType.COURT_OPINIONS]: (
      recordItem:
        | RecordType
        | SerializedObituaryTeaserRecord
        | ContactDetail
        | PACERDocketResult
        | OpinionResult,
      index: number
    ) => {
      const item = recordItem as OpinionResult;

      const dateUtil = new DateUtil();
      const filedDate = item.dateFiled
        ? dateUtil.parseDateFromString(item.dateFiled, 'MM/dd/yyyy') ?? null
        : null;
      const filedYear = item.dateFiled
        ? new Date(item.dateFiled).getFullYear()
        : null;

      const titleWithCitation = item.caseName
        ? `${item.caseName}${
            item.court_citation_string && filedYear
              ? ` (${item.court_citation_string} ${filedYear})`
              : filedYear
              ? ` (${filedYear})`
              : ''
          }`
        : '';

      const citationsText = Array.isArray(item.citation)
        ? item.citation.join(', ')
        : item.citation || '';

      const firstOpinion =
        item.opinions && item.opinions.length > 0 ? item.opinions[0] : null;
      const opinionType = firstOpinion?.type
        ? cleanOpinionType(firstOpinion.type)
        : null;
      const opinionSnippet = firstOpinion?.snippet || '';

      const dateField =
        filedDate || item.status ? (
          <Box sx={{ mb: 0.5 }}>
            <Text variant="body2" color="text.primary">
              {filedDate && (
                <>
                  <Text component="span" sx={{ fontWeight: 600 }}>
                    Filed:
                  </Text>{' '}
                  {filedDate}
                </>
              )}
              {filedDate && item.status && (
                <Text component="span" sx={{ fontWeight: 600 }}>
                  {' '}
                  |{' '}
                </Text>
              )}
              {item.status && (
                <>
                  <Text component="span" sx={{ fontWeight: 600 }}>
                    Status:
                  </Text>{' '}
                  {item.status}
                </>
              )}
            </Text>
          </Box>
        ) : null;

      const docketField =
        item.docketNumber || citationsText ? (
          <Box sx={{ mb: 0.5 }}>
            <Text variant="body2" color="text.primary">
              {item.docketNumber && (
                <>
                  <Text component="span" sx={{ fontWeight: 600 }}>
                    Docket:
                  </Text>{' '}
                  {item.docketNumber}
                </>
              )}
              {item.docketNumber && citationsText && (
                <Text component="span" sx={{ fontWeight: 600 }}>
                  {' '}
                  | Citations:
                </Text>
              )}
              {citationsText && <> {citationsText}</>}
            </Text>
          </Box>
        ) : null;

      const simpleFields = [
        {
          label: 'Judges',
          value: item.judge,
          condition: Boolean(item.judge),
        },
        {
          label: opinionType || '',
          value: '',
          condition: Boolean(opinionType),
          isBold: true,
        },
        {
          label: '',
          value: opinionSnippet,
          condition: Boolean(opinionSnippet),
          isSnippet: true,
        },
      ];

      const courtOpinionTeaserHandler = () => {
        if (item.cluster_id !== null && item.cluster_id !== undefined) {
          handleCourtOpinionTeaserClick(item.cluster_id.toString());
        }
      };

      return (
        <ReportChatCard
          title={titleWithCitation}
          reportType="court-opinions"
          icon={<Gavel color="primary" fontSize="small" />}
          clickAction={courtOpinionTeaserHandler}
          description={
            <Stack spacing={0.5}>
              {dateField}
              {docketField}
              {simpleFields.map((field, fieldIndex) => {
                if (!field.condition) {
                  return null;
                }

                if (field.isSnippet) {
                  return (
                    <Box key={fieldIndex} sx={{ mb: 0.5 }}>
                      <Text
                        variant="body2"
                        color="text.primary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {field.value}
                      </Text>
                    </Box>
                  );
                }

                if (field.isBold) {
                  return (
                    <Box key={fieldIndex} sx={{ mb: 0.5 }}>
                      <Text
                        variant="body2"
                        color="text.primary"
                        sx={{ fontWeight: 600 }}
                      >
                        {field.label}
                      </Text>
                    </Box>
                  );
                }

                return (
                  <TeaserField
                    key={fieldIndex}
                    label={field.label}
                    value={field.value}
                    condition={field.condition}
                  />
                );
              })}
            </Stack>
          }
          disabled={false}
          key={item.cluster_id || `teaser-${index}`}
        />
      );
    },
  };

  useEffect(() => {
    if (
      searchData?.fName === params?.first_name &&
      searchData?.lName === params?.last_name
    ) {
      setLocalSearchData(searchData);
    }
  }, [searchData, params?.first_name, params?.last_name]);

  const fetchers = () => {
    return (
      <>
        {collectionType === CollectionType.PUBLIC_RECORDS && (
          <PersonFetcher
            setPersonResults={setPersonResults}
            searchData={localSearchData}
          />
        )}
        {collectionType === CollectionType.OBITUARIES && (
          <ObituaryFetcher
            setObituaryResults={setObituaryResults}
            searchData={localSearchData}
          />
        )}
        {collectionType === CollectionType.FAMILY_SEARCH && (
          <FamilySearchFetcher
            setFamilySearchResults={setFamilySearchResults}
            searchData={localSearchData}
          />
        )}
        {collectionType === CollectionType.CONTACTS && (
          <ContactFetcher
            setContactResults={setContactResults}
            searchData={localSearchData}
          />
        )}
        {collectionType === CollectionType.COURT_CASES && (
          <CourtCaseFetcher
            setCourtCaseResults={setCourtCaseResults}
            searchData={localSearchData}
          />
        )}
        {collectionType === CollectionType.COURT_OPINIONS && (
          <CourtOpinionFetcher
            setCourtOpinionResults={setCourtOpinionResults}
            searchData={localSearchData}
          />
        )}
      </>
    );
  };

  const handleShowMoreClick = () => {
    setAmountOfResultsToShow(
      amountOfResultsToShow + NUMBER_OF_TEASER_RESULTS_TO_SHOW
    );
  };

  const handleChatPersonTeaserClick = (bvid: string) => {
    handleChatTeaserClick(bvid || '');
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.chat_teaser.type,
      subtype: CHATBV_GA4_EVENTS.chat_teaser.subTypes.person_card_click,
    });
  };

  const handleObituaryTeaserClick = async (id: string) => {
    try {
      const response = await reportGetters.obituary({
        queryParams: new URLSearchParams({ id }),
      });
      const permalink = response?.report?.permalink;
      if (permalink) {
        handleObituaryClick(permalink);
      }
    } catch (error) {
      logError('Error getting obituary permalink:', error as Error);
    }
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.chat_teaser.type,
      subtype: CHATBV_GA4_EVENTS.chat_teaser.subTypes.obituary_card_click,
    });
  };

  const handleFamilySearchTeaserClick = async (familySearchId: string) => {
    try {
      const response = await reportGetters.ancestor({
        queryParams: new URLSearchParams({ family_search_id: familySearchId }),
      });
      const permalink = response?.report?.permalink;
      if (permalink) {
        handleAncestorClick(permalink);
      }
    } catch (error) {
      logError('Error getting ancestor permalink:', error as Error);
    }
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.chat_teaser.type,
      subtype: CHATBV_GA4_EVENTS.chat_teaser.subTypes.ancestor_card_click,
    });
  };

  const handleContactTeaserClick = async (id: string) => {
    try {
      const response = await reportGetters.contact({
        queryParams: new URLSearchParams({ contact_id: id }),
      });
      const permalink = response?.report?.permalink;
      if (permalink) {
        handleContactClick(permalink);
      }
    } catch (error) {
      logError('Error getting contact permalink:', error as Error);
    }
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.chat_teaser.type,
      subtype: CHATBV_GA4_EVENTS.chat_teaser.subTypes.contact_card_click,
    });
  };

  const handleCourtCaseTeaserClick = (docketId: string) => {
    const courtCaseUrl = `${constants.links.baseUrl}/dashboard/court-cases/cases/${docketId}`;
    window.open(courtCaseUrl, '_blank', 'noopener,noreferrer');
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.chat_teaser.type,
      subtype: CHATBV_GA4_EVENTS.chat_teaser.subTypes.court_case_card_click,
    });
  };

  const handleCourtOpinionTeaserClick = (opinionId: string) => {
    const courtOpinionUrl = `${constants.links.baseUrl}/dashboard/court-cases/opinions/${opinionId}`;
    window.open(courtOpinionUrl, '_blank', 'noopener,noreferrer');
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.chat_teaser.type,
      subtype: CHATBV_GA4_EVENTS.chat_teaser.subTypes.court_opinion_card_click,
    });
  };

  return (
    <StyledStack
      divider={<Divider />}
      sx={
        collapsed
          ? {
              borderColor: `${theme.palette.primary.main}1F`,
              background: `linear-gradient(90deg, ${theme.palette.primary.contrastText} 0%, ${theme.palette.primary.light} 100%)`,
            }
          : {}
      }
    >
      <Stack spacing={2} pb={2}>
        <Stack direction="row" justifyContent="space-between">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {collectionTypeIcons[collectionType]}
            <Text variant="h3" sx={{ my: 0, mx: 1 }}>
              {COLLECTION_TYPE_LABELS[collectionType]}
            </Text>
          </Box>
          {collapsed ? (
            <CollapsedButton
              variant="outlined"
              size="small"
              onClick={() => setCollapsed(false)}
            >
              <KeyboardArrowDown sx={{ fontSize: 30 }} />
            </CollapsedButton>
          ) : (
            <CollapsedButton
              variant="outlined"
              size="small"
              onClick={() => setCollapsed(true)}
            >
              <KeyboardArrowUp sx={{ fontSize: 30 }} />
            </CollapsedButton>
          )}
        </Stack>
        {Boolean(params?.first_name || params?.last_name) && (
          <Text variant="h4" fontWeight="normal">
            <StyledSpan sx={{ mr: 1 }}>Results for:</StyledSpan>
            {`${params?.first_name ?? ''} ${params?.middle_name ?? ''} ${
              params?.last_name ?? ''
            }`.trim()}
          </Text>
        )}
        {!collapsed && params ? (
          <Stack
            direction={isMobile ? 'column' : 'row'}
            justifyContent="space-between"
            spacing={isMobile ? 2 : 0}
          >
            <SourceSelect
              collectionType={collectionType}
              setCollectionType={setCollectionType}
            />
            <StyledButton
              variant="outlined"
              size="small"
              onClick={() => handleFiltersClick(localSearchData)}
            >
              <Tune sx={{ mr: 1 }} />
              Filters
            </StyledButton>
          </Stack>
        ) : null}
      </Stack>
      {!collapsed
        ? (resultsArraysByCollectionType[collectionType] || [])
            .slice(0, amountOfResultsToShow)
            .map(renderFunctionByCollectionType[collectionType] || (() => null))
        : null}
      {!collapsed
        ? (resultsArraysByCollectionType[collectionType] || []).length >
            amountOfResultsToShow && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleShowMoreClick}
              sx={{ mt: 2 }}
            >
              Show More
              <KeyboardArrowDown />
            </Button>
          )
        : null}
      {!collapsed ? fetchers() : null}
    </StyledStack>
  );
};
